import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const ENTITLEMENT_LABELS = {
  crm: "CRM with lead management",
  appointments: "Appointment booking",
  voice_agents: "AI voice agents",
  marketing_studio: "AI marketing studio",
  automations: "Core automations",
  analytics_reports: "Analytics & reporting",
};

const gbp = (pence) => `£${(pence / 100).toFixed(pence % 100 ? 2 : 0)}`;

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("month");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setPlans(d.plans))
      .catch(() => setPlans([]));
  }, []);

  const hasAnnual = (plans || []).some((p) => (p.interval_options || {}).year);
  const priceFor = (p) => {
    const opts = p.interval_options || {};
    if (interval === "year" && opts.year) return opts.year.price_pence;
    return p.price_pence;
  };
  const monthlyEquivalent = (p) => {
    const opts = p.interval_options || {};
    if (interval === "year" && opts.year) return Math.round(opts.year.price_pence / 12);
    return null;
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="scroll-mt-16 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Pricing</p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
            Simple monthly pricing
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-500 sm:text-lg">
            Subscriptions are in test mode — no real charges are taken.
          </p>
        </div>

        {hasAnnual && (
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-full border border-neutral-200 p-1" data-testid="pricing-interval-toggle">
              {[["month", "Monthly"], ["year", "Annual — save 15%"]].map(([v, label]) => (
                <button
                  key={v}
                  data-testid={`pricing-interval-${v}`}
                  onClick={() => setInterval(v)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                    interval === v ? "bg-[#111111] text-white" : "text-neutral-500 hover:text-[#111111]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {(plans || []).map((p) => (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative flex flex-col rounded-3xl border p-8 transition-all hover:-translate-y-1 ${
                p.featured
                  ? "border-[#111111] bg-[#111111] text-white shadow-[0_32px_64px_-24px_rgba(0,0,0,0.35)]"
                  : "border-neutral-200 bg-white hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.12)]"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-[#111111] shadow-md ring-1 ring-neutral-200">
                  Most popular
                </span>
              )}
              <h3 className={`text-sm font-bold uppercase tracking-widest ${p.featured ? "text-neutral-300" : "text-neutral-500"}`}>
                {p.name}
              </h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold tracking-tight" data-testid={`pricing-price-${p.name.toLowerCase()}`}>{gbp(priceFor(p))}</span>
                <span className="text-xs font-medium text-neutral-400">{interval === "year" && (p.interval_options || {}).year ? "per year" : "per month"}</span>
              </div>
              {interval === "year" && monthlyEquivalent(p) && (
                <p className="mt-1 text-xs font-medium text-neutral-400">that's {gbp(monthlyEquivalent(p))}/month, billed annually</p>
              )}
              <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-neutral-300" : "text-neutral-500"}`}>
                {p.description}
              </p>
              <ul className="mt-7 flex-1 space-y-3">
                {(p.entitlements || []).map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm font-medium">
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full ${p.featured ? "bg-white text-[#111111]" : "bg-[#111111] text-white"}`}>
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span className={p.featured ? "text-neutral-100" : "text-neutral-700"}>{ENTITLEMENT_LABELS[f] || f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={user ? "/dashboard/settings#billing" : "/signup"}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 rounded-full py-3.5 text-center text-sm font-semibold transition-all ${
                  p.featured
                    ? "bg-white text-[#111111] hover:bg-neutral-200"
                    : "border border-neutral-300 text-[#111111] hover:border-neutral-400 hover:bg-neutral-50"
                }`}
              >
                {user ? "Choose " + p.name : "Get started"}
              </a>
            </div>
          ))}
          {plans === null && (
            <p className="col-span-full text-center text-sm text-neutral-400">Loading plans…</p>
          )}
        </div>
      </div>
    </section>
  );
}
